import { useIntl } from "react-intl";
import { DetailsField } from "components/DetailsField";
import { HistoryDetailsCardTransactionsProps } from "./types";
import { messages } from "./messages";
import { useFormatter } from "hooks/utils/useFormatter";
import {
  CARD_TRANSACTION_DETAIL_TYPE,
  CARD_TRANSACTION_STATUS,
  CardTransactionDetails,
  GET_INVOICE_SERVICE,
  GET_INVOICE_TYPE,
} from "@letsbit/malcolmlb";
import { formatLastFour } from "utils/paymentCardUtils";
import dayjs from "dayjs";
import { DownloadInvoice } from "../DownloadInvoice";

export const HistoryDetailsCardTransactions: React.FC<
  HistoryDetailsCardTransactionsProps
> = ({ data: transaction }) => {
  const { formatMessage } = useIntl();
  const { formatValue } = useFormatter();

  const formattedDate = dayjs(transaction.date).format("HH:mm DD.MM.YYYY");

  const hasTaxes =
    transaction.extra_data.details && transaction.extra_data.details.length > 1;

  const taxes = hasTaxes
    ? transaction.extra_data.details?.map((d: CardTransactionDetails) =>
        d.type === CARD_TRANSACTION_DETAIL_TYPE.BASE
          ? {
              label: formatMessage(messages.purchaseAmount),
              value: `${formatValue(
                d.amount,
              )} ${transaction.currency_id.toUpperCase()}`,
            }
          : {
              label: d.name,
              value: `+ ${formatValue(
                d.amount,
              )} ${transaction.currency_id.toUpperCase()}`,
            },
      )
    : [];
  const fees = transaction.extra_data.fee
    ? {
        label: formatMessage(messages.cardFee),
        value: `+${formatValue(
          transaction.extra_data.fee,
          transaction.extra_data?.fee_currency?.precision,
        )} ${transaction.extra_data?.fee_currency?.ticker_id.toUpperCase()}`,
      }
    : undefined;

  let taxesAndFees = taxes;
  if (fees) {
    taxesAndFees = taxesAndFees ? [...taxesAndFees, fees] : taxesAndFees;
  }

  const getStatus = (state?: string) => {
    let stClass = "text-[--color-warning]";
    let st = formatMessage(messages.statusInProgress);

    switch (state) {
      case CARD_TRANSACTION_STATUS.APPROVED:
        stClass = "text-[--color-success]";
        st = formatMessage(messages.approved);
        break;
      case CARD_TRANSACTION_STATUS.REJECTED:
        stClass = "text-[--color-error]";
        st = formatMessage(messages.rejected);
        break;
    }
    return <span className={stClass}>{st}</span>;
  };
  return (
    <div className="flex flex-col gap-y-6 font-['Source_Sans_Pro']">
      <DetailsField
        value={transaction.extra_data.merchant_name ?? ""}
        label={formattedDate}
        className="flex-col-reverse"
      />
      <div className="flex flex-col gap-4">
        {taxesAndFees && taxesAndFees?.length > 0 && (
          <>
            <h3 className="text-xl font-bold text-[--color-neutral-0]">
              {formatMessage(messages.detailsTransactionTitle)}
            </h3>
            {taxesAndFees?.map((i) => (
              <div className="flex w-full justify-between text-lg text-[--color-neutral-0]">
                <label>{i.label}</label>
                <h4>{i.value}</h4>
              </div>
            ))}
          </>
        )}
        <div className="flex w-full justify-between text-2xl font-bold text-[--color-neutral-0]">
          <label>{formatMessage(messages.amount)}</label>
          <h4>
            {formatValue(transaction.amount, transaction.precision) +
              " " +
              transaction.ticker_id.toUpperCase()}
          </h4>
        </div>
      </div>
      <DetailsField
        label={formatMessage(messages.card)}
        value={formatLastFour(transaction.extra_data.last_four || "")}
      />
      {transaction.extra_data.merchant_mcc && (
        <DetailsField
          label={formatMessage(messages.sector)}
          value={formatLastFour(transaction.extra_data.merchant_mcc || "")}
        />
      )}
      <DetailsField
        label={formatMessage(messages.status)}
        value={getStatus(transaction.extra_data.state)}
      />
      <DetailsField
        label={formatMessage(messages.movementId)}
        value={transaction.tid ?? ""}
        copy
      />
      <DownloadInvoice
        service={GET_INVOICE_SERVICE.KAIBACORP}
        type={GET_INVOICE_TYPE.CARD_TRANSACTION}
        external_txid={transaction.tid ?? ""}
      />
    </div>
  );
};
